import { User, Save, BadgeCheck, Building2, Crown, Plus, ExternalLink, Mail, Briefcase, CreditCard } from "lucide-react";

export default function DashboardAccountTab({
  owner,
  ownerEditForm,
  setOwnerEditForm,
  isVerified,
  sendVerificationCode,
  verifyingEmail,
  handleSaveOwnerProfile,
  savingOwner,
  venues,
  selectVenue,
  setShowAddVenueModal,
  hasMultipleVenues,
  isPremium,
  hasAnyPremiumVenue
}) {
  return (
    <div className="bg-white rounded-xl p-5 border border-gray-100 space-y-6">
      <div>
        <h3 className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
          <User className="w-5 h-5 text-brand" />
          Your Account
        </h3>
        <p className="text-sm text-gray-500">Manage your personal details and account settings</p>
      </div>

      {/* Personal Details */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Your Name</label>
          <input
            type="text"
            value={ownerEditForm.name}
            onChange={(e) => setOwnerEditForm({ ...ownerEditForm, name: e.target.value })}
            className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand text-sm"
            data-testid="owner-name"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
          <div className="flex items-center gap-2">
            <input
              type="email"
              value={ownerEditForm.email}
              onChange={(e) => setOwnerEditForm({ ...ownerEditForm, email: e.target.value })}
              className="flex-1 px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand text-sm"
              data-testid="owner-email"
            />
            {isVerified ? (
              <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                <BadgeCheck className="w-3 h-3" /> Verified
              </span>
            ) : (
              <button
                onClick={sendVerificationCode}
                disabled={verifyingEmail}
                className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded hover:bg-amber-100"
              >
                Verify
              </button>
            )}
          </div>
        </div>

        <button
          onClick={handleSaveOwnerProfile}
          disabled={savingOwner}
          className="btn-primary flex items-center gap-2"
          data-testid="save-owner-profile"
        >
          <Save className="w-4 h-4" />
          {savingOwner ? "Saving..." : "Save Profile"}
        </button>
      </div>

      {/* Venues List */}
      <div className="border-t pt-5">
        <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
          <Building2 className="w-4 h-4" />
          Your Venues ({venues.length})
        </h4>
        <div className="space-y-2">
          {venues.map((venue) => (
            <div
              key={venue.id}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-brand/10 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-brand" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 text-sm">{venue.name}</span>
                    {venue.is_premium && (
                      <span className="text-xs bg-gradient-to-r from-amber-500 to-orange-500 text-white px-1.5 py-0.5 rounded flex items-center gap-1">
                        <Crown className="w-3 h-3" /> Premium
                      </span>
                    )}
                    {venue.is_verified && <BadgeCheck className="w-4 h-4 text-brand" />}
                  </div>
                  <span className="text-xs text-gray-500">{venue.location?.city}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => selectVenue(venue)}
                  className="text-xs text-brand hover:underline"
                >
                  Manage
                </button>
                <a
                  href={`/venue/${venue.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1.5 text-gray-400 hover:text-brand"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          ))}
        </div>
        <button
          onClick={() => setShowAddVenueModal(true)}
          className="mt-3 w-full flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg border border-dashed border-gray-300 text-gray-600 hover:border-brand hover:text-brand transition-colors text-sm"
        >
          <Plus className="w-4 h-4" />
          Add Another Venue
        </button>
      </div>

      {/* Corporate Account CTA - For owners with multiple venues */}
      {hasMultipleVenues && (
        <div className="border-t pt-5">
          <div className="bg-gradient-to-r from-brand/5 to-brand/10 rounded-xl p-4 border border-brand/20">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-brand/20 flex items-center justify-center flex-shrink-0">
                <Briefcase className="w-5 h-5 text-brand" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Need a Corporate Account?</h4>
                <p className="text-sm text-gray-600 mb-3">
                  Managing multiple venues? Contact us for special corporate pricing, dedicated support, and advanced analytics across all your locations.
                </p>
                <a
                  href="mailto:corporate@wafw.net?subject=Corporate Account Inquiry"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-brand hover:text-brand-dark"
                >
                  <Mail className="w-4 h-4" />
                  Contact us at corporate@wafw.net
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Status */}
      <div className="border-t pt-5">
        <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
          <CreditCard className="w-4 h-4" />
          Subscription
        </h4>
        {isPremium ? (
          <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200">
            <div className="flex items-center gap-2 mb-2">
              <Crown className="w-5 h-5 text-amber-600" />
              <span className="font-semibold text-amber-800">Premium Active</span>
            </div>
            <p className="text-sm text-amber-700">
              {hasAnyPremiumVenue 
                ? `You have ${venues.filter(v => v.is_premium).length} premium venue(s)` 
                : "Your subscription gives you access to premium features"}
            </p>
          </div>
        ) : (
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-sm text-gray-600 mb-2">You're on the free plan</p>
            <button
              onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
              className="text-sm font-medium text-brand hover:text-brand-dark flex items-center gap-1"
            >
              <Crown className="w-4 h-4" /> Upgrade to Premium
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
